import { BillStatus } from '../../__generated__/globalTypes';
import { formatCalendarDate } from '../formatting';

import type { RecordPaneStatus } from '../../components/entity/RecordPane';
import type { CalendarDate } from '@internationalized/date';

interface IBillRecordRead<TBill> {
  loading: boolean;
  error: unknown;
  bill: TBill | null | undefined;
}

/**
 * Which state the record shell shows for a bill read. A failed read wins over
 * a cached bill; `getBill` answers `null` for an id the viewer cannot see,
 * which is the not-found state.
 */
export function billRecordStatus<TBill>({ loading, error, bill }: IBillRecordRead<TBill>): RecordPaneStatus {
  if (error) {
    return 'error';
  }
  if (bill) {
    return 'ready';
  }
  return loading ? 'loading' : 'not-found';
}

export interface IBillDateSource {
  status: BillStatus;
  dueDate: CalendarDate;
  transactions: ReadonlyArray<{ date: CalendarDate }>;
}

/**
 * The hero's Date cell: the latest linked payment's date once the bill is
 * paid, "Due <date>" otherwise. A paid bill with no linked payment on the
 * read falls back to its due date rather than showing nothing.
 */
export function billDateCellValue(bill: IBillDateSource): string {
  const paidOn = bill.status === BillStatus.Paid ? latestDate(bill.transactions) : null;
  return paidOn ? formatCalendarDate(paidOn, 'medium') : `Due ${formatCalendarDate(bill.dueDate, 'medium')}`;
}

function latestDate(transactions: ReadonlyArray<{ date: CalendarDate }>): CalendarDate | null {
  return transactions.reduce<CalendarDate | null>(
    (latest, { date }) => (latest === null || date.compare(latest) > 0 ? date : latest),
    null
  );
}

export interface IBillPayment {
  amount: string;
  merchant?: { id: string } | null;
}

export interface IBillPaymentSource<TPayment extends IBillPayment = IBillPayment> {
  merchant?: { id: string } | null;
  transactions: ReadonlyArray<TPayment>;
}

/**
 * The bill's linked transactions the merchant's spending stats count: debits
 * (positive amounts) at the bill's own merchant. A refund or a payment booked
 * under another merchant is not in that aggregate. The graph does not expose
 * whether a linked row is ignored, so an ignored payment is included here
 * although the stats leave it out — an accepted approximation.
 */
export function billPaymentsAtMerchant<TPayment extends IBillPayment>(bill: IBillPaymentSource<TPayment>): TPayment[] {
  const merchantId = bill.merchant?.id;
  if (!merchantId) {
    return [];
  }
  return bill.transactions.filter(
    (transaction) => Number(transaction.amount) > 0 && transaction.merchant?.id === merchantId
  );
}
